import React from 'react'
import { Button } from '@/components/ui/button'
import { RefreshCw, Home } from 'lucide-react'

type Props = {
  children: React.ReactNode
  fallback?: React.ReactNode
}

type State = {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary para captura de erros de componentes React.
 *
 * Uso:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <div className="max-w-md w-full text-center space-y-6">
              <div className="space-y-2">
                <h1 className="text-xl font-semibold text-foreground">Algo deu errado</h1>
                <p className="text-muted-foreground">
                  {import.meta.env.MODE === 'development'
                    ? this.state.error?.message
                    : 'Ocorreu um erro inesperado. Tente novamente.'}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={this.handleReset} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Tentar novamente
                </Button>
                <Button variant="primary" onClick={this.handleGoHome} className="gap-2">
                  <Home className="h-4 w-4" />
                  Ir para início
                </Button>
              </div>

              {import.meta.env.MODE === 'development' && this.state.error?.stack && (
                <details className="mt-6 p-4 bg-muted rounded-lg text-xs text-left">
                  <summary className="cursor-pointer font-medium text-muted-foreground">
                    Detalhes do erro (apenas dev)
                  </summary>
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap text-destructive">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
